import type { ReactionAmount } from "../data/models/schemas";
import {
    bigDecimalToString,
    type BigDecimal,
    type LegacyBigDecimal,
} from "./bigDecimal";

export type ReactionAmountKind = "mass" | "moles" | "volume";

export type ReactionAmountEntry = {
    kind: ReactionAmountKind;
    unit: string;
    value?: BigDecimal;
};

const MASS_UNITS = new Set(["mg", "g", "kg"]);
const MOLES_UNITS = new Set(["mol", "mmol"]);
const VOLUME_UNITS = new Set(["ml", "l"]);

const DEFAULT_UNITS: Record<ReactionAmountKind, string> = {
    mass: "g",
    moles: "mol",
    volume: "ml",
};

const DISPLAY_DECIMAL_PLACES = 2;

const normalizeUnit = (unit?: string): string =>
    unit?.trim().toLowerCase() || "";

const trimTrailingDecimalZeros = (value: string): string =>
    value.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");

export const formatReactionAmountValue = (
    value?: BigDecimal | number | LegacyBigDecimal | null
): string | undefined => {
    const valueString = bigDecimalToString(value);
    if (!valueString) {
        return undefined;
    }

    const numericValue = Number(valueString);
    if (!Number.isFinite(numericValue)) {
        return valueString.trim() || undefined;
    }

    const roundedValue = numericValue.toFixed(DISPLAY_DECIMAL_PLACES);
    if (numericValue !== 0 && Number(roundedValue) === 0) {
        return `<${(10 ** -DISPLAY_DECIMAL_PLACES).toFixed(
            DISPLAY_DECIMAL_PLACES
        )}`;
    }

    const trimmed = trimTrailingDecimalZeros(roundedValue);
    return trimmed === "-0" ? "0" : trimmed;
};

export const getReactionAmountKindFromUnit = (
    unit?: string
): ReactionAmountKind | undefined => {
    const normalizedUnit = normalizeUnit(unit);

    if (MASS_UNITS.has(normalizedUnit)) {
        return "mass";
    }
    if (MOLES_UNITS.has(normalizedUnit)) {
        return "moles";
    }
    if (VOLUME_UNITS.has(normalizedUnit)) {
        return "volume";
    }

    return undefined;
};

export const getReactionAmountEntry = (
    amount?: ReactionAmount
): ReactionAmountEntry | undefined => {
    if (!amount) {
        return undefined;
    }

    if (amount.massValue !== undefined || amount.massUnit) {
        return {
            kind: "mass",
            unit: normalizeUnit(amount.massUnit) || DEFAULT_UNITS.mass,
            value: amount.massValue,
        };
    }

    if (amount.molesValue !== undefined || amount.molesUnit) {
        return {
            kind: "moles",
            unit: normalizeUnit(amount.molesUnit) || DEFAULT_UNITS.moles,
            value: amount.molesValue,
        };
    }

    if (amount.volumeValue !== undefined || amount.volumeUnit) {
        return {
            kind: "volume",
            unit: normalizeUnit(amount.volumeUnit) || DEFAULT_UNITS.volume,
            value: amount.volumeValue,
        };
    }

    return undefined;
};

export const buildReactionAmountFromUnit = (
    unit: string,
    value: BigDecimal | undefined
): ReactionAmount | undefined => {
    const kind = getReactionAmountKindFromUnit(unit);
    if (!kind) {
        return undefined;
    }

    if (value === undefined) {
        return {
            [`${kind}Unit`]: unit,
        } as ReactionAmount;
    }

    return {
        [`${kind}Value`]: value,
        [`${kind}Unit`]: unit,
    } as ReactionAmount;
};

export const sanitizeReactionAmount = (
    amount?: ReactionAmount
): ReactionAmount | undefined => {
    const entry = getReactionAmountEntry(amount);
    if (!entry?.value) {
        return undefined;
    }

    return buildReactionAmountFromUnit(entry.unit, entry.value);
};

export const formatReactionAmount = (
    amount?: ReactionAmount
): { label: string; kind: ReactionAmountKind } | undefined => {
    const entry = getReactionAmountEntry(amount);
    const value = formatReactionAmountValue(entry?.value);

    if (!entry || !value) {
        return undefined;
    }

    return {
        label: `${value} ${entry.unit}`,
        kind: entry.kind,
    };
};
